// src/components/AdminDashboard.js
import { useEffect, useState } from 'react';
import { 
  getAdminStats, 
  getRecentActivity, 
  fetchStudentRequests,
  updateRequestStatus 
} from '../../../services/adminService';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [statsData, activityData, requestsData] = await Promise.all([
        getAdminStats(),
        getRecentActivity(),
        fetchStudentRequests()
      ]);
      
      setStats(statsData);
      setActivity(activityData || []);
      setRequests(requestsData.slice(0, 5)); // Son 5 istek
    } catch (err) {
      setError('Could not load data. Please refresh the page.');
      console.error('Dashboard yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      setActionLoading(true);
      await updateRequestStatus(id, status);
      
      // Talebi güncelle
      setRequests(prev => 
        prev.map(req => 
          req._id === id ? { ...req, status } : req
        )
      );
      
      // Aktivite listesini yenile
      const activityData = await getRecentActivity();
      setActivity(activityData || []);
      
    } catch (err) {
      setError('Operation failed: ' + (err.response?.data?.msg || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Pending';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'pending': return 'pending';
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
        </div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <p>System statistics and recent activities</p>
        </div>
        <div className="header-actions">
          <button 
            className="refresh-btn" 
            onClick={loadDashboardData}
            disabled={actionLoading}
          >
            <span className="refresh-icon">🔄</span>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="dashboard-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-inner">
              <div className="stat-icon total">
                <div className="icon-bg"></div>
                👥
              </div>
              <div className="stat-content">
                <h3>Total Students</h3>
                <div className="stat-value">{stats.totalStudents || 0}</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-card-inner">
              <div className="stat-icon pending">
                <div className="icon-bg"></div>
                👨‍🏫
              </div>
              <div className="stat-content">
                <h3>Total Teachers</h3>
                <div className="stat-value">{stats.totalTeachers || 0}</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-card-inner">
              <div className="stat-icon approved">
                <div className="icon-bg"></div>
                ⏳
              </div>
              <div className="stat-content">
                <h3>Pending Requests</h3>
                <div className="stat-value">{stats.pendingRequests || 0}</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-card-inner">
              <div className="stat-icon rejected">
                <div className="icon-bg"></div>
                👤
              </div>
              <div className="stat-content">
                <h3>Total Users</h3>
                <div className="stat-value">{stats.totalUsers || 0}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {/* Recent Registration Requests */}
        <div className="content-section">
          <div className="section-header">
            <h3>Recent Registration Requests</h3>
            <button 
              className="view-all-btn"
              onClick={() => navigate('/admin/student-requests')}
            >
              View All →
            </button>
          </div>
          <div className="requests-list">
            {requests.length === 0 ? (
              <div className="empty-list">No registration requests yet</div>
            ) : (
              requests.map((request, index) => (
                <div 
                  key={request._id || index} 
                  className={`request-item ${getStatusClass(request.status)}`}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="request-avatar">
                    <div className="avatar-placeholder">
                      {request.name?.charAt(0) || request.studentName?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="request-info">
                    <span className="student-name">{request.name || request.studentName}</span>
                    <span className="course-name">{request.email}</span>
                    <span className="request-date">{formatDate(request.createdAt)}</span>
                  </div>
                  <div className="request-status">
                    <span className={`status-badge ${request.status}`}>
                      {getStatusText(request.status)}
                    </span>
                    {request.status === 'pending' && (
                      <div className="request-actions">
                        <button 
                          className="btn btn-success"
                          onClick={() => handleStatusUpdate(request._id, 'approved')}
                          disabled={actionLoading}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn btn-danger"
                          onClick={() => handleStatusUpdate(request._id, 'rejected')}
                          disabled={actionLoading}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="content-section">
          <div className="section-header">
            <h3>Recent Activity</h3>
            <button className="view-all-btn">View All →</button>
          </div>
          <div className="activity-list">
            {activity.length === 0 ? (
              <div className="empty-list">No activity yet</div>
            ) : (
              activity.map((item, index) => (
                <div 
                  key={index} 
                  className="activity-item"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="activity-icon">{item.icon}</div>
                  <div className="activity-content">
                    <p>{item.description}</p>
                    <span className="activity-time">{item.time}</span>
                  </div>
                  <div className="activity-badge">New</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;